// src/components/layers/inquiry/InquiryLayer.tsx

import React, { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import Draggable from "react-draggable"
import { Box, Button, Paper, Typography, styled } from "@mui/material"
import SendIcon from "@mui/icons-material/Send"
import { useGlobalSnackbar } from "@core/contexts/GlobalSnackbarProvider"
import { isWebPlatform } from "@/core/platform"
import { ModalHeader, modalContainerStyle } from "@shared/components/common/ModalStyles"
import MessageDialog from "@shared/components/common/MessageDialog"
import { useSelector } from "react-redux"
import { hospitalInfoSelector } from "@/features/hospital/hospitalSelectors"
import InquiryDetachedPortal from "./InquiryDetachedPortal"

interface InquiryLayerProps {
  open: boolean
  onClose: () => void
  resetTrigger?: number
  activationKey?: number
  reservedPopupWindow?: Window | null
  preferDetachedOnWeb?: boolean
}

const TARGET_URL = "https://99cloud.channel.io/home"
const LAYOUT_KEY = "inquiryLayer.layout"
const DEFAULT_WIDTH = 420
const DEFAULT_HEIGHT = 768 // 기존 720 + 헤더 높이
const MIN_HEIGHT = 520
const MAX_HEIGHT = 1080
const IFRAME_LOAD_TIMEOUT_MS = 10000
const DEFAULT_POSITION = { x: 0, y: 0 }

const WebRemoteSupportNotice = styled(Typography)({
  width: "100%",
  minWidth: 0,
  fontSize: "0.9rem",
  fontWeight: 500,
  lineHeight: 1.5,
  textAlign: "left",
})

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

const InquiryLayer: React.FC<InquiryLayerProps> = ({
  open,
  onClose,
  resetTrigger = 0,
  activationKey = 0,
  reservedPopupWindow = null,
  preferDetachedOnWeb = false,
}) => {
  const nodeRef = useRef<HTMLDivElement>(null)
  const lastActivationKeyRef = useRef(activationKey)
  const [iframeElement, setIframeElement] = useState<HTMLIFrameElement | null>(null)
  const [detachedRetryKey, setDetachedRetryKey] = useState(0)
  const [detachedLoadFailed, setDetachedLoadFailed] = useState(false)
  const [loadErrorDialogOpen, setLoadErrorDialogOpen] = useState(false)
  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT })
  const [position, setPosition] = useState(DEFAULT_POSITION)
  const hospitalInfo = useSelector(hospitalInfoSelector)
  const { showSnackbar } = useGlobalSnackbar()
  const shouldDetach = !isWebPlatform() || preferDetachedOnWeb

  const openInquiryInDirectWindow = useCallback(() => {
    const openedWindow = window.open(TARGET_URL, "_blank", "width=450,height=720,scrollbars=yes,resizable=yes")
    if (openedWindow) {
      try {
        openedWindow.focus()
      } catch {
        /* 무시 */
      }
      return true
    }
    return false
  }, [])

  const handleIframeRef = useCallback((node: HTMLIFrameElement | null) => {
    setIframeElement(node)
  }, [])

  const handleDetachedLoadTimeout = useCallback(() => {
    showSnackbar("문의 페이지 로드가 지연되고 있습니다. 잠시 후 다시 시도해 주세요.", "warning")
  }, [showSnackbar])

  const persistLayout = useCallback((nextSize: { width: number; height: number }, nextPosition: { x: number; y: number }) => {
    try {
      localStorage.setItem(
        LAYOUT_KEY,
        JSON.stringify({
          width: nextSize.width,
          height: nextSize.height,
          x: nextPosition.x,
          y: nextPosition.y,
        })
      )
    } catch (error) {
      console.error("문의 레이어 레이아웃 저장 실패:", error)
    }
  }, [])

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LAYOUT_KEY)
      if (!saved) return

      const parsed = JSON.parse(saved)
      const width = typeof parsed?.width === "number" ? parsed.width : DEFAULT_WIDTH
      const rawHeight = typeof parsed?.height === "number" ? parsed.height : DEFAULT_HEIGHT
      const height = clamp(rawHeight, MIN_HEIGHT, MAX_HEIGHT)
      const x = typeof parsed?.x === "number" ? parsed.x : 0
      const y = typeof parsed?.y === "number" ? parsed.y : 0

      setSize({ width, height })
      setPosition({ x, y })
    } catch (error) {
      console.error("문의 레이어 레이아웃 로드 실패:", error)
    }
  }, [])

  useEffect(() => {
    if (!open || !iframeElement) return

    const iframe = iframeElement
    let timeoutId: number | undefined

    iframe.setAttribute("referrerpolicy", "no-referrer-when-downgrade")
    iframe.setAttribute("crossorigin", "anonymous")

    iframe.onload = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      setDetachedLoadFailed(false)
      console.log("Inquiry iframe loaded successfully")
    }

    iframe.onerror = error => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      console.error("Inquiry iframe load error:", error)

      if (shouldDetach) {
        setDetachedLoadFailed(true)
        showSnackbar("문의 페이지를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.", "error")
        return
      }

      setLoadErrorDialogOpen(true)
    }

    timeoutId = window.setTimeout(() => {
      console.warn("Inquiry iframe load timeout")

      if (shouldDetach) {
        setDetachedLoadFailed(true)
        handleDetachedLoadTimeout()
        return
      }

      setLoadErrorDialogOpen(true)
    }, IFRAME_LOAD_TIMEOUT_MS)

    // 이벤트 핸들러를 먼저 등록한 뒤 src 지정(레이스 컨디션 방지)
    if (!iframe.src || iframe.src !== TARGET_URL) {
      iframe.src = TARGET_URL
    }

    return () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId)
      }
      iframe.onload = null
      iframe.onerror = null
    }
  }, [handleDetachedLoadTimeout, iframeElement, open, shouldDetach, showSnackbar])

  useEffect(() => {
    if (!open) return

    setPosition(DEFAULT_POSITION)
  }, [open, resetTrigger])

  useEffect(() => {
    if (open) {
      return
    }

    setDetachedLoadFailed(false)
    setLoadErrorDialogOpen(false)
  }, [open])

  useEffect(() => {
    const activationChanged = lastActivationKeyRef.current !== activationKey
    lastActivationKeyRef.current = activationKey

    if (!activationChanged || !open || !shouldDetach || !detachedLoadFailed) {
      return
    }

    setDetachedLoadFailed(false)
    setLoadErrorDialogOpen(false)
    setDetachedRetryKey(prev => prev + 1)
  }, [activationKey, detachedLoadFailed, open, shouldDetach])

  const handleLoadErrorConfirm = () => {
    openInquiryInDirectWindow()
    setLoadErrorDialogOpen(false)
    onClose()
  }

  const handleLoadErrorClose = () => {
    setLoadErrorDialogOpen(false)
  }

  const handleDetachedOpenFailed = useCallback(() => {
    showSnackbar("문의하기 외부창을 열지 못했습니다.", "error")
    onClose()
  }, [onClose, showSnackbar])

  const handleRemoteRequest = async () => {
    const ipc = window.electron?.ipcRenderer
    if (!ipc) {
      alert("원격요청은 시그마차트 프로그램에서만 가능합니다.")
      return
    }

    try {
      const RETRY_DELAY_MS = 1000
      const MAX_RETRY = 10

      const getAnyDeskIdWithRetry = async (): Promise<string | null> => {
        for (let attempt = 1; attempt <= MAX_RETRY; attempt++) {
          const id = await ipc.invoke("launch-anydesk-and-get-id")
          console.log(`AnyDesk ID attempt ${attempt}/${MAX_RETRY}:`, id)
          if (id) return id
          if (attempt < MAX_RETRY) {
            await new Promise<void>(resolve => setTimeout(resolve, RETRY_DELAY_MS))
          }
        }
        return null
      }

      const anydeskId = await getAnyDeskIdWithRetry()
      const hospitalName = (hospitalInfo?.hospitalName || "").trim()
      console.log("[문의레이어] 병원명:", hospitalName || "(빈 값)")

      if (!anydeskId) {
        alert("AnyDesk ID를 찾을 수 없습니다.")
        return
      }

      const result = await ipc.invoke("automate-channel-chat", anydeskId, hospitalName)
      console.log("[문의레이어] 자동화 IPC 결과:", result)

      if (result?.success) {
        console.log("자동화 성공: AnyDesk ID 전송 완료")
      } else {
        console.error("자동화 실패:", result?.error)
        alert(`자동 입력 실패: ${result?.error}\n\nAnyDesk ID: ${anydeskId}\n(클립보드에 복사됨)\n수동으로 입력해 주세요.`)
      }
    } catch (error) {
      console.error("원격요청 처리 중 오류:", error)
      alert("원격요청 처리 중 오류가 발생했습니다.")
    }
  }

  useEffect(() => {
    if (!open || shouldDetach) {
      return
    }

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose()
      }
    }

    window.addEventListener("keydown", handleEsc)
    return () => window.removeEventListener("keydown", handleEsc)
  }, [onClose, open, shouldDetach])

  const handleResizeMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startY = event.clientY
    const startHeight = size.height

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY
      const nextHeight = clamp(startHeight + deltaY, MIN_HEIGHT, MAX_HEIGHT)
      setSize(prev => {
        const updated = { ...prev, height: nextHeight }
        persistLayout(updated, position)
        return updated
      })
    }

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove)
      document.removeEventListener("mouseup", onMouseUp)
    }

    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
  }

  const inquiryHeader = (
    <Box
      sx={{
        px: 2,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid #ddd",
        flexShrink: 0,
        position: "relative",
        zIndex: 10,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
      }}
    >
      {!isWebPlatform() && (
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: "0.9rem", fontWeight: 500, flexGrow: 1, minWidth: 0 }}>
          AI가 아닙니다.
          <br /> 실시간으로 개발 원장이 직접 답변드립니다.
        </Typography>
      )}
      {isWebPlatform() ? (
        <WebRemoteSupportNotice color="text.secondary">
          웹차트사용중
          <br />
          웹차트에서는 문의만 하실 수 있습니다.
          <br />
          원격지원 요청은 차트 프로그램에서 가능합니다.
        </WebRemoteSupportNotice>
      ) : (
        <Button
          onClick={handleRemoteRequest}
          endIcon={<SendIcon />}
          sx={{
            fontSize: "0.9rem",
            display: "flex",
            gap: "4px",
            alignItems: "center",
            justifyContent: "center",
            pl: 3,
            pr: 2,
            py: 0.5,
            borderRadius: "16px",
            background: "linear-gradient(90deg, rgb(226, 231, 114) 48.41%, rgb(206, 231, 114))",
            color: "rgb(0, 0, 0)",
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            minWidth: 0,
            whiteSpace: "nowrap",
            "& .MuiButton-endIcon": {
              ml: 2,
              "> *:nth-of-type(1)": {
                fontSize: 30,
                transform: "rotate(-30deg) translateY(-5px)",
              },
            },
            "&:hover": {
              background: "linear-gradient(90deg, rgb(226, 231, 114) 48.41%, rgb(206, 231, 114))",
              filter: "brightness(0.97)",
            },
          }}
        >
          원격지원
          <br /> 요청하기
        </Button>
      )}
    </Box>
  )

  const inquiryFrame = (
    <div
      style={{
        flex: 1,
        overflow: "hidden",
        padding: 0,
      }}
    >
      <iframe
        key={detachedRetryKey}
        ref={handleIframeRef}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: "block",
        }}
        title="문의하기"
        allow="fullscreen; microphone; camera"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
      />
    </div>
  )

  if (!open) return null

  if (shouldDetach) {
    return (
      <InquiryDetachedPortal
        open={open}
        title="문의하기"
        activationKey={activationKey}
        reservedPopupWindow={reservedPopupWindow}
        onClose={onClose}
        onOpenFailed={handleDetachedOpenFailed}
      >
        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff", overflow: "hidden" }}>
          {inquiryHeader}
          {inquiryFrame}
        </Box>
      </InquiryDetachedPortal>
    )
  }

  return createPortal(
    <>
      <div
        className="layer-container"
        style={{
          alignItems: "center",
          justifyContent: "center",
          padding: "0",
        }}
      >
        <Draggable
          handle=".layer-header"
          nodeRef={nodeRef as React.RefObject<HTMLElement>}
          cancel={".layer-close-button, .layer-header-actions"}
          position={position}
          onDrag={(e, data) => {
            setPosition({ x: data.x, y: data.y })
          }}
          onStop={(e, data) => {
            const nextPos = { x: data.x, y: data.y }
            setPosition(nextPos)
            persistLayout(size, nextPos)
          }}
        >
          <Paper
            ref={nodeRef}
            sx={{
              ...modalContainerStyle,
              width: size.width,
              height: size.height,
              maxWidth: "95vw",
              maxHeight: "95vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              pointerEvents: "auto",
            }}
          >
            <ModalHeader title="문의하기" onClose={onClose} />
            {inquiryHeader}
            {inquiryFrame}
            <div
              onMouseDown={handleResizeMouseDown}
              style={{
                height: 10,
                cursor: "ns-resize",
                background: "transparent",
                flexShrink: 0,
              }}
            />
          </Paper>
        </Draggable>
      </div>
      <MessageDialog
        open={loadErrorDialogOpen}
        onClose={handleLoadErrorClose}
        onConfirm={handleLoadErrorConfirm}
        message={"문의 페이지 로드에 실패했습니다. 새 창에서 열까요?"}
        type={"warning"}
        showCancel
        confirmText="새 창 열기"
        cancelText="취소"
      />
    </>,
    document.body
  )
}

export default InquiryLayer
